import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

interface EventProps {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

export const useAnalytics = ({ trackPageView = false }: { trackPageView?: boolean } = {}) => {
  const location = useLocation();

  // Track page views automatically when location changes
  useEffect(() => {
    if (process.env.REACT_APP_GA_TRACKING_ID && trackPageView) {
      ReactGA.send({ hitType: "pageview", page: location.pathname });
    }
  }, [location, trackPageView]);

  // Track feature usage
  const trackFeature = (featureName: string, eventProps: Record<string, string> = {}) => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.event('feature_use', {
        feature_name: featureName,
        ...eventProps
      });
    }
  };

  // Track user interactions
  const trackUserInteraction = (action: string, label?: string) => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.event({
        category: 'User Interaction',
        action,
        label,
      });
    }
  };

  // Track form submissions
  const trackFormSubmission = (formName: string, success: boolean) => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.event({
        category: 'Form',
        action: success ? 'Submit Success' : 'Submit Failure',
        label: formName,
      });
    }
  };

  // Track errors
  const trackError = (error: string, fatal: boolean = false) => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.event({
        category: 'Error',
        action: error,
        label: fatal ? 'Fatal' : 'Non-Fatal',
      });
    }
  };

  // Track custom events
  const trackEvent = ({ category, action, label, value }: EventProps) => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.event({
        category,
        action,
        label,
        value,
      });
      if(label){
          ReactGA.event({
            category: category,
            action: action,
            label: label,
            value: value,
          });
      }
    }
  };

  // Track purchases/transactions
  const trackPurchase = (productName: string, value: number, currency: string = 'USD') => {
    if (process.env.REACT_APP_GA_TRACKING_ID) {
      ReactGA.event({
        category: 'Purchase',
        action: 'Complete',
        label: `${productName} (${currency})`,
        value,
      });
    }
  };

  return {
    trackFeature,
    trackUserInteraction,
    trackFormSubmission,
    trackError,
    trackEvent,
    trackPurchase,
  };
};